<template>
  <basic-dialog
    :show="show"
    @close="$emit('close')"
    max-width="1600px"
    draggable
  >
    <template v-slot:title>Batch Purchase Invoice Payments Create</template>
    <v-row>
      <v-col sm="2">
        <date-picker
          customLabel="Payment Date"
          @onDateSelected="onDateSelected"
          :gtDateString="paymentDate"
          :outlined="outlinedInputs"
          hide-details
        ></date-picker>
      </v-col>
      <v-col>
        <v-select
          label="Payee Type"
          v-model="payeeType"
          :items="payeeTypesList"
          :outlined="outlinedInputs"
          item-text="name"
          item-value="name"
          hide-details
        />
      </v-col>
      <v-col v-if="false">
        <v-autocomplete
          label="Payee"
          v-model="payeeId"
          :items="payeeList"
          :outlined="outlinedInputs"
          item-text="name"
          item-value="id"
        ></v-autocomplete>
      </v-col>
      <v-col>
        <v-text-field
          label="Payment Ref"
          v-model="paymentReference"
          :outlined="outlinedInputs"
          hide-details
        ></v-text-field>
      </v-col>
      <v-col>
        <v-text-field
          label="Notes"
          v-model="notes"
          :outlined="outlinedInputs"
          hide-details
        ></v-text-field>
      </v-col>
    </v-row>
    <v-row>
      <v-col>
        <payment-type-select
          v-model="paymentType"
          :outlined="outlinedInputs"
          hide-details
        ></payment-type-select>
      </v-col>
      <v-col v-if="showPaymentMethods">
        <payment-method-list-select
          v-model="paymentMethodId"
          :outlined="outlinedInputs"
        ></payment-method-list-select>
      </v-col>
      <v-col>
        <taxes-list-select
          v-model="vatCode"
          :outlined="outlinedInputs"
          hide-details
        ></taxes-list-select>
      </v-col>
      <v-col sm="3" v-if="false">
        <v-autocomplete
          label="Currency"
          :outlined="outlinedInputs"
          v-model="currencyCode"
          :items="currencyList"
          hide-details
        ></v-autocomplete>
      </v-col>
      <v-col sm="2">
        <v-text-field
          label="Amount"
          v-model="total"
          :outlined="outlinedInputs"
          disabled
          hide-details
        ></v-text-field>
      </v-col>
      <v-col sm="2" v-if="showBatchExchangeRate">
        <v-text-field
          label="Exchange Rate"
          v-model="batchExchangeRate"
          type="number"
          min="0"
          :outlined="outlinedInputs"
          hide-details
          @input="onBatchExchangeRateChange"
        ></v-text-field>
      </v-col>
    </v-row>
    <v-alert
      dense
      type="warning"
      :value="mixedSuppliers"
      transition="scale-transition"
    >
      Payee doesn't match selected items!
    </v-alert>
    <v-row>
      <v-col cols="12">
        <v-container fluid py-0>
          <v-row justify="end" align="center">
            <v-select
              v-if="showGroupedView"
              :items="groupByOptions"
              v-model="selectedGroupedBy"
              label="Group by"
              item-text="name"
              return-object
              :outlined="$store.getters.localSettings('outlinedInputs')"
              hide-details
            ></v-select>
            <v-switch
              prepend-icon="mdi-view-list"
              class="mr-3"
              label="Group View"
              v-model="showGroupedView"
            ></v-switch>
            <list-visible-columns-and-filter
              :visible-columns="visibleColumns"
              :selectable-headers="selectableHeaders"
              v-model="search"
              @change="onVisibleColumnsChange"
            ></list-visible-columns-and-filter>
          </v-row>
        </v-container>
      </v-col>
    </v-row>

    <v-row v-if="showGroupedView">
      <v-col class="py-0">
        <list-grouped :group="selectedGroupedBy" :items="items">
          <template v-slot:table="{ items }">
            <v-card>
              <pi-table-batch-pay
                :items="items"
                :headers="filteredHeaders"
                :search="search"
                @onAmountChange="onAmountChange"
                :selected-items="selected"
                @updateSelection="onUpdateSelection"
              ></pi-table-batch-pay>
            </v-card>
          </template>
        </list-grouped>
      </v-col>
    </v-row>
    <v-row v-else>
      <v-col class="py">
        <v-card>
          <v-data-table
            :items="items"
            item-key="id"
            show-select
            v-model="selected"
            :headers="filteredHeaders"
            :search="search"
          >
            <template v-slot:item.amount="{ item }" v-if="false">
              <v-text-field
                type="number"
                v-model="item.amount"
                min="0"
                class="pt-1 ma-0 short-input"
                @change="onAmountChange(item)"
              ></v-text-field>
            </template>
            <template v-slot:item.approved="{ item }">
              <v-checkbox
                v-model="item.approved"
                primary
                hide-details
                :disabled="true"
                class="ma-0"
              ></v-checkbox>
            </template>
            <template v-slot:item.itineraryReference="{ item }">
              <reference-label
                v-model="item.itineraryReference"
                :itinerary-id="item.itinerary ? item.itinerary.id : ''"
                new-tab
              ></reference-label>
            </template>

            <template v-slot:item.vatAmount="{ item }">
              {{ Number(item.vatAmount).toFixed(2) }}
            </template>
            <template v-slot:item.netAmount="{ item }">
              {{ Number(item.netAmount).toFixed(2) }}
            </template>
            <template v-slot:item.paid="{ item }">
              {{ Number(item.paid).toFixed(2) }}
            </template>
            <template v-slot:item.balance="{ item }">
              {{ Number(item.balance).toFixed(2) }}
            </template>
            <template v-slot:item.paymentAmount="{ item }">
              <v-text-field
                type="number"
                v-model="item.paymentAmount"
                min="0"
                class="pt-1 ma-0 short-input"
                @change="onAmountChange(item)"
              ></v-text-field>
            </template>
            <template v-slot:item.exchangeRate="{ item }">
              <v-text-field
                type="number"
                v-model="item.exchangeRate"
                min="0"
                class="pt-1 ma-0 short-input"
                @change="onAmountChange(item)"
              ></v-text-field>
            </template>
          </v-data-table>
        </v-card>
      </v-col>
    </v-row>

    <div v-if="debug">
      <code> {{ selected }} </code>
    </div>

    <template v-slot:actions>
      <v-btn
        v-if="$store.getters.hasAccessTo('finances:xero_export')"
        @click="onCreateAndExportToXero"
        :disabled="!isValid || loading"
        :loading="loading"
        >Create &amp; Export to Xero</v-btn
      >
      <v-btn
        color="primary"
        :disabled="!isValid || loading"
        @click="onCreateAndClose"
        :loading="loading"
        >Create &amp; Close</v-btn
      >
      <v-btn
        :disabled="!isValid || loading"
        @click="onCreate(false)"
        :loading="loading"
        >Create</v-btn
      >
    </template>
  </basic-dialog>
</template>

<script>
import { removeGQLTypename } from "../../../../helpers";
import BasicDialog from "../../shared/BasicDialog";
import DatePicker from "../../shared/DatePicker";
import PaymentTypeSelect from "../../shared/PaymentTypeSelect";
import TaxesListSelect from "../../shared/TaxesListSelect";
import CurrencyListSelect from "../../shared/CurrencyListSelect";
import TaskStatusMixin from "../../../../mixins/TaskStatusMixin";
import ListVisibleColumnsAndFilter from "../../shared/ListVisibleColumnsAndFilter";
import ReferenceLabel from "../../shared/labels/ReferenceLabel";
import gql from "graphql-tag";
import ListGrouped from "../../shared/ListGrouped.vue";

import PiTableBatchPay from "./PiTableBatchPay.vue";
import PaymentMethodListSelect from "../../shared/PaymentMethodListSelect.vue";
const CREATE_SUPPLIER_PAYMENTS = gql`
  mutation ($input: SupplierPaymentsInput) {
    createSupplierPayments(input: $input) {
      id
    }
  }
`;
export default {
  name: "batch-payments-dialog",

  props: {
    show: {
      type: Boolean,
      default: () => false,
    },
    invoices: {
      type: Array,
      default: () => [],
    },
    columns: {
      type: Array,
      default: () => [],
    },
  },

  mixins: [TaskStatusMixin],

  components: {
    BasicDialog,
    DatePicker,
    PaymentTypeSelect,
    TaxesListSelect,
    CurrencyListSelect,
    ListVisibleColumnsAndFilter,
    ReferenceLabel,
    ListGrouped,
    PiTableBatchPay,
    PaymentMethodListSelect,
  },

  data() {
    return {
      debug: false,
      showGroupedView: false,
      selectedGroupedBy: {
        name: "Supplier",
        getKey: (item) => item.supplierName,
        getLabel: (item) => `Supplier: ${item.supplierName}`,
      },
      groupByOptions: [
        {
          name: "Supplier",
          getKey: (item) => item.supplierName,
          getLabel: (item) => `Supplier: ${item.supplierName}`,
        },
      ],
      search: "",
      loading: false,
      paymentDate: new Date().toISOString(false),
      paymentReference: "",
      selected:
        this.invoices.length > 0
          ? [
              ...this.invoices.map((item) => ({
                ...item,
                amount: item.balance,
              })),
            ]
          : [],
      amount: 0,
      payeeType: "SUPPLIER",
      payeeTypesList: ["SUPPLIER", "HEAD OFFICE"],
      payeeId: "",
      currencyCode: "",
      paymentType: "",
      paymentMethodId: "",
      vatCode: "",
      notes: "",
      items:
        this.invoices.length > 0
          ? this.invoices.map((item) => ({
              ...item,
              paymentAmount: item.balance,
            }))
          : [],
      visibleColumns: this.$store.getters.localSettings(this.$options.name, [
        "number",
        "supplierName",
        "headOfficeName",
        "itineraryReference",
        "currencyCode",
        "paymentAmount",
      ]),
      batchExchangeRate: null,
    };
    closeAfterCreate: false;
  },

  computed: {
    showPaymentMethods() {
      return this.$store.getters.hasAccessTo("finances:payment_methods");
    },
    selectableHeaders() {
      return [
        ...this.columns.filter(
          (c) => c.name != "actions" && c.name != "menuActions"
        ),
      ];
    },
    headers() {
      const amountCol = {
        text: "Payment Amount",
        value: "paymentAmount",
        name: "paymentAmount",
      };
      return [...this.columns.filter((c) => c.name != "actions"), amountCol];
    },

    filteredHeaders() {
      return [
        ...this.headers.filter((h) => this.visibleColumns.indexOf(h.name) > -1),
      ];
    },

    outlinedInputs() {
      return this.$store.getters.localSettings("outlinedInputs");
    },
    isValid() {
      return (
        this.selected.length > 0 &&
        this.paymentType != "" &&
        this.paymentReference != ""
      );
    },
    mixedSuppliers() {
      if (this.payeeId != "" && this.selected.length > 0) {
        const payee = this.payeeList.find((item) => item.id == this.payeeId);
        if (payee && payee.isHeadOffice) {
          const mixedHeadOffices = this.selected.find(
            (item) => item.supplier.headOfficeId != this.payeeId
          );
          return mixedHeadOffices != undefined;
        } else {
          const mixedSuppliers = this.selected.find(
            (item) => item.supplier.id != this.payeeId
          );
          return mixedSuppliers != undefined;
        }
      } else {
        return false;
      }
    },
    total() {
      return this.selected
        .reduce(
          (t, item) => t + Number(item.paymentAmount ? item.paymentAmount : 0),
          0
        )
        .toFixed(2);
    },
    currencyList() {
      const list = this.invoices.reduce((l, item) => {
        if (l.indexOf(item.currencyCode) == -1) {
          l.push(item.currencyCode);
        }
        return l;
      }, []);
      return list;
    },
    mixedItems() {
      if (this.payeeType == "SUPPLIER") {
        const uniqSuppliers = new Set(
          this.selected.map((item) => item.supplier.id)
        );
        return uniqSuppliers.size > 1;
      } else {
        const uniqHeadOffices = new Set(
          this.selected.map((item) => item.supplier.headOfficeId)
        );
        return uniqHeadOffices.size > 1;
      }
      return false;
    },
    payeeList() {
      return this.invoices.reduce((p, item) => {
        if (this.payeeType == "SUPPLIER") {
          const supplierPayee = {
            name: item.supplierName,
            id: item.supplier.id,
            isHeadOffice: false,
          };
          if (!p.find((item) => supplierPayee.name == item.name)) {
            p.push(supplierPayee);
          }
        } else {
          // check for headOffice
          if (item.headOfficeName != "") {
            const hoPayee = {
              name: item.headOfficeName,
              id: item.supplier.headOfficeId,
              isHeadOffice: true,
            };
            if (!p.find((item) => hoPayee.name == item.name)) {
              p.push(hoPayee);
            }
          }
        }

        return p;
      }, []);
    },
    showBatchExchangeRate() {
      // Show only when exchangeRate column is visible and all selected rows have the same currency
      const exchangeRateVisible =
        this.visibleColumns.indexOf("exchangeRate") > -1;
      if (!exchangeRateVisible || this.selected.length === 0) {
        return false;
      }

      // Check if all selected rows have the same currency
      const currencyCodes = new Set(
        this.selected.map((item) => item.currencyCode)
      );
      return currencyCodes.size === 1;
    },
  },

  watch: {
    invoices(newVal) {
      this.items = [
        ...newVal.map((item) => ({
          ...item,
          paymentAmount: item.paymentAmount ? item.paymentAmount : item.balance,
        })),
      ];
      this.selected = [...this.items];
    },
    selected() {
      // Initialize or update batchExchangeRate when selected items change
      this.initializeBatchExchangeRate();
    },
    showBatchExchangeRate(newVal) {
      // Initialize batchExchangeRate when the computed property becomes true
      if (newVal) {
        this.initializeBatchExchangeRate();
      }
    },
  },

  mounted() {
    // Initialize batchExchangeRate on component mount if conditions are met
    this.initializeBatchExchangeRate();
  },

  methods: {
    initializeBatchExchangeRate() {
      // Initialize batchExchangeRate when showBatchExchangeRate is true and selected items exist
      if (this.showBatchExchangeRate && this.selected.length > 0) {
        const firstExchangeRate = this.selected[0].exchangeRate;
        if (firstExchangeRate != null) {
          this.batchExchangeRate = firstExchangeRate;
        }
      }
    },
    onCreateAndExportToXero() {
      this.onCreate(true);
    },
    onUpdateSelection(payload) {
      //this.selected = [...payload.selected];
      //remove group from all selected items
      this.selected = this.selected.filter(
        (item) => !payload.items.includes(item)
      );
      //add new selected items
      this.selected = [...this.selected, ...payload.selected];
    },
    onDateSelected(payload) {
      this.paymentDate = payload;
    },
    onCreateAndClose() {
      this.closeAfterCreate = true;
      this.onCreate();
    },
    onCreate(exportToXero = false) {
      const input = {
        commonPaymentRef: this.paymentReference,
        notes: this.notes,
        lodgementDate: this.paymentDate,
        paymentType: this.paymentType,
        paymentMethodId: this.paymentMethodId,
        taxType: this.vatCode,
        currencyCode: this.currencyCode,
        payeeId: this.payeeId,
        payeeType: this.payeeType,
        exportToXero: exportToXero,
        invoices: this.selected.map((item) => ({
          id: item.id,
          amount: Number(item.paymentAmount),
          exchangeRate: Number(item.exchangeRate),
        })),
      };
      //console.log(input);
      this.$apollo
        .mutate({
          mutation: CREATE_SUPPLIER_PAYMENTS,
          variables: { input },
        })
        .then(({ data }) => {
          //console.log(data.createSupplierPayments.id);
          this.loading = true;
          this.taskID = data.createSupplierPayments.id;
          this.checkStatus = true;
        })
        .catch((err) => {
          this.loading = false;
          console.error(err);
        });
    },

    onAmountChange(item) {
      //refresh totals
      this.selected = [...this.selected];
    },
    onBatchExchangeRateChange() {
      // Update all selected rows with the new exchangeRate
      if (
        this.batchExchangeRate != null &&
        this.batchExchangeRate !== "" &&
        this.selected.length > 0
      ) {
        const exchangeRateValue = Number(this.batchExchangeRate);
        if (!isNaN(exchangeRateValue)) {
          this.selected.forEach((item) => {
            item.exchangeRate = exchangeRateValue;
          });
          // Update items array to reflect the change
          this.items.forEach((item) => {
            const selectedItem = this.selected.find((s) => s.id === item.id);
            if (selectedItem) {
              item.exchangeRate = exchangeRateValue;
            }
          });
          // Trigger reactivity
          this.selected = [...this.selected];
          this.items = [...this.items];
        }
      }
    },
    taskStatusCompleteHandler(data) {
      //console.log("task completed: ", data);
      this.loading = false;
      if (
        data.taskStatus.status == "SUCCESS" ||
        data.taskStatus.status == "FINISHED"
      ) {
        this.$store.dispatch("showAppSnackbar", {
          msg: data.taskStatus.notes,
          type: "success",
        });
      } else if (data.taskStatus.status == "ERROR") {
        this.$store.dispatch("showAppSnackbar", {
          msg: data.taskStatus.notes,
          type: "error",
        });
      }
      if (this.closeAfterCreate) {
        this.closeAfterCreate = false;
        this.$emit("close");
      }
    },
    onVisibleColumnsChange(event) {
      this.visibleColumns = [...event];
      this.$store.dispatch("updateLocalSettings", {
        [this.$options.name]: event,
      });
    },
  },
};
</script>

<style></style>



// WEBPACK FOOTER //
// src/components/gt/finances/purchase-invoice-payments/BatchPaymentsDialog.vue